/**
 * ConnectDataStep — Step 3 of the Add-Building modal.
 *
 * Reuses the existing ESPM wiring (`useCompanyESPMdata`,
 * `PortfolioManagerConnectModal`) without redesigning it. The goal here is
 * *optional attachment* — the user can pick an ESPM property, connect a new
 * ESPM account, or skip entirely.
 *
 * Edge cases handled:
 *   - Already-attached ESPM property → surface a "Attached to another
 *     building" warning, still allow proceed (per Leo's locked-in design).
 *   - No ESPM accounts yet → explainer + "Connect Portfolio Manager" that
 *     opens the existing `PortfolioManagerConnectModal`.
 *   - ESPM API failure → non-blocking; user can still skip.
 */

import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, PlusCircle } from "lucide-react";

import { Button } from "@/components/shared/ui/button";
import { Text } from "@/components/shared/ui/text";
import { useMeQuery } from "@/queries/queries";
import { useCompanyESPMdata } from "@/queries/useCompanyQueries";
import PortfolioManagerConnectModal from "@/pages/BuildingDetail/Stages/DataCollection/PortfolioManagerConnect/Modal";
import type { AddBuildingDraft, AddBuildingEspmSelection } from "./types";

interface ConnectDataStepProps {
  draft: AddBuildingDraft;
  onBack: () => void;
  onContinue: () => void;
  onSelectEspm: (selection: AddBuildingEspmSelection | null) => void;
  onSkipEspm: () => void;
}

interface EspmPropertyRow {
  id: string;
  buildingName: string | null;
  address1: string | null;
  primaryFunction: string | null;
  /** Backend field name for "this ESPM property is already linked to a
   * building in our DB". Exists on the existing serializer as `building`. */
  building: string | null;
}

const ConnectDataStep: React.FC<ConnectDataStepProps> = ({
  draft,
  onBack,
  onContinue,
  onSelectEspm,
  onSkipEspm,
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const queryClient = useQueryClient();
  const { data: user } = useMeQuery();
  const companyId = user?.company?.id ?? "";
  const {
    data: espmAccounts,
    isLoading,
    isError,
  } = useCompanyESPMdata(companyId, true);
  const [connectOpen, setConnectOpen] = useState(false);

  const espmProperties: EspmPropertyRow[] =
    espmAccounts?.flatMap(
      (account: { espmProperties?: EspmPropertyRow[] }) =>
        account.espmProperties ?? [],
    ) ?? [];

  const unattachedProperties = espmProperties.filter(
    (p) => !(p.building && p.building.trim().length > 0),
  );

  // When the sheet closes, invalidate so freshly-connected properties show.
  useEffect(() => {
    if (!connectOpen) queryClient.invalidateQueries();
  }, [connectOpen, queryClient]);

  // Auto-stage a single newly-shared unattached property after the modal
  // closes. No API call here — this only writes to the Add-Building draft.
  const snapshotAtOpenRef = useRef<number | null>(null);
  const prevConnectOpenRef = useRef(connectOpen);
  const autoStagedRef = useRef(false);

  useEffect(() => {
    const wasOpen = prevConnectOpenRef.current;
    if (!wasOpen && connectOpen) {
      snapshotAtOpenRef.current = unattachedProperties.length;
      autoStagedRef.current = false;
    }
    prevConnectOpenRef.current = connectOpen;
    if (
      !connectOpen &&
      !autoStagedRef.current &&
      !draft.espmSelection &&
      snapshotAtOpenRef.current === 0 &&
      unattachedProperties.length === 1
    ) {
      autoStagedRef.current = true;
      const property = unattachedProperties[0];
      onSelectEspm({
        propertyId: property.id,
        buildingName: property.buildingName ?? "",
        address: property.address1 ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectOpen, unattachedProperties.length, draft.espmSelection]);

  const hasProperties = espmProperties.length > 0;
  const selectedId = draft.espmSelection?.propertyId ?? null;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-lg font-semibold leading-tight focus:outline-none"
        >
          Connect your data
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Energy Star Portfolio Manager (ESPM) is NYC&apos;s official tool for
          tracking building energy data. You can link it now or later.
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading your Portfolio Manager properties...
        </div>
      )}

      {isError && (
        <div
          role="status"
          className="rounded-md border border-warning/30 bg-warning/10 p-3 text-sm flex gap-2"
        >
          <AlertTriangle
            className="h-4 w-4 shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <span>
            We couldn&apos;t reach Portfolio Manager right now. You can skip
            this step and connect later.
          </span>
        </div>
      )}

      {!isLoading && !hasProperties && (
        <div className="rounded-md border border-border bg-muted/40 p-4 flex flex-col gap-3">
          <p className="text-sm">
            To file LL97 or LL88, your building&apos;s data must be shared with
            CarbonClear from Portfolio Manager. This usually takes a few
            minutes.
          </p>
          <div className="flex flex-row flex-wrap gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConnectOpen(true)}
            >
              Connect Portfolio Manager
            </Button>
          </div>
        </div>
      )}

      {hasProperties && (
        <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto pr-2">
          {espmProperties.map((property) => {
            const alreadyAttached = Boolean(
              property.building && property.building.trim().length > 0,
            );
            const isSelected = selectedId === property.id;
            const disabled = alreadyAttached;
            let stateClass =
              "border-border bg-background hover:bg-accent hover:text-accent-foreground";
            if (disabled) {
              stateClass =
                "border-border bg-muted text-muted-foreground cursor-not-allowed";
            } else if (isSelected) {
              stateClass = "border-primary bg-primary/5";
            }
            return (
              <button
                key={property.id}
                type="button"
                disabled={disabled}
                aria-pressed={isSelected}
                onClick={() => {
                  if (disabled) return;
                  if (isSelected) {
                    onSelectEspm(null);
                  } else {
                    onSelectEspm({
                      propertyId: property.id,
                      buildingName: property.buildingName ?? "",
                      address: property.address1 ?? "",
                    });
                  }
                }}
                className={`w-full text-left rounded-md border px-4 py-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${stateClass}`}
              >
                <div className="flex justify-between gap-3">
                  <div>
                    <div className="font-medium">
                      {property.buildingName ||
                        property.address1 ||
                        "Unnamed ESPM property"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {property.address1}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground self-center">
                    {property.primaryFunction}
                  </div>
                </div>
                {alreadyAttached && (
                  <div className="mt-2 flex items-start gap-1.5 text-xs">
                    <AlertTriangle
                      className="h-3.5 w-3.5 shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                    <span>
                      This property is already linked to another building in
                      your portfolio.
                    </span>
                  </div>
                )}
              </button>
            );
          })}
          <div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConnectOpen(true)}
            >
              <PlusCircle className="h-4 w-4 mr-1" aria-hidden="true" />
              Connect a new ESPM account
            </Button>
          </div>
        </div>
      )}

      <Text className="text-xs text-muted-foreground">
        You can always connect ESPM later from the building&apos;s Data
        Collection page.
      </Text>

      <div className="flex flex-row justify-between gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <div className="flex flex-row gap-3">
          <Button type="button" variant="outline" onClick={onSkipEspm}>
            Skip for now
          </Button>
          <Button
            type="button"
            onClick={onContinue}
            disabled={!draft.espmSelection && !draft.espmSkipped}
          >
            Continue
          </Button>
        </div>
      </div>

      <PortfolioManagerConnectModal
        open={connectOpen}
        onOpenChange={setConnectOpen}
      />
    </div>
  );
};

export default ConnectDataStep;
